import { toast } from "react-toastify";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import logger from "../helpers/logger";

/**
 * Lightweight Authentication Monitor Service
 * Minimal overhead, only checks when needed
 */
class AuthMonitorLite {
  constructor() {
    this.msalInstance = null;
    this.currentAccount = null;
    this.isInitialized = false;
    this.lastCheck = null;
    this.cachedStatus = null;

    // Much more conservative configuration - aggressive buffer for production
    this.CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes instead of 5-10
    this.WARNING_THRESHOLD_MINUTES = 25; // Large buffer - but we'll suppress toasts
    this.CRITICAL_THRESHOLD_MINUTES = 15; // 15 minute buffer - eliminates all race conditions
  }

  /**
   * Initialize with minimal setup
   */
  initialize(msalInstance, account) {
    logger.log("🔐 Initializing Lightweight Auth Monitor");

    this.msalInstance = msalInstance;
    this.currentAccount = account;
    this.isInitialized = true;

    // No automatic intervals - only check when requested
    logger.log("✅ Lightweight auth monitor ready (on-demand only)");
  }

  /**
   * Check auth status only when explicitly requested
   */
  async checkAuthStatus() {
    if (!this.msalInstance || !this.currentAccount) {
      return { status: "unknown", message: "Not initialized" };
    }

    // Cache for 5 minutes to avoid repeated checks
    const now = Date.now();
    if (
      this.cachedStatus &&
      this.lastCheck &&
      now - this.lastCheck < 5 * 60 * 1000
    ) {
      return this.cachedStatus;
    }

    try {
      // Verify account still exists before attempting refresh
      const accounts = this.msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        this.cachedStatus = { status: "unknown", message: "No accounts found" };
        return this.cachedStatus;
      }

      // Update currentAccount if it's stale
      const activeAccount = accounts.find(
        (acc) => acc.homeAccountId === this.currentAccount?.homeAccountId
      ) || accounts[0];
      
      if (activeAccount.homeAccountId !== this.currentAccount?.homeAccountId) {
        logger.log("🔄 AuthMonitorLite: Account reference updated");
        this.currentAccount = activeAccount;
      }

      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: ["User.Read", "Mail.Send"],
        account: this.currentAccount,
      });

      if (!tokenResponse || !tokenResponse.expiresOn) {
        this.cachedStatus = { status: "unknown", message: "No token info" };
        return this.cachedStatus;
      }

      const expiresInMs = new Date(tokenResponse.expiresOn).getTime() - now;
      const expiresInMinutes = expiresInMs / (1000 * 60);

      let status;
      if (expiresInMinutes <= 0) {
        status = {
          status: "expired",
          expiresIn: 0,
          message: "Session expired",
        };
      } else if (expiresInMinutes <= this.CRITICAL_THRESHOLD_MINUTES) {
        status = {
          status: "critical",
          expiresIn: expiresInMinutes,
          message: `${Math.round(expiresInMinutes)} minutes remaining`,
        };
      } else if (expiresInMinutes <= this.WARNING_THRESHOLD_MINUTES) {
        status = {
          status: "warning",
          expiresIn: expiresInMinutes,
          message: `${Math.round(expiresInMinutes)} minutes remaining`,
        };
      } else {
        status = {
          status: "healthy",
          expiresIn: expiresInMinutes,
          message: `${Math.round(expiresInMinutes)} minutes remaining`,
        };
      }

      this.cachedStatus = status;
      this.lastCheck = now;
      return status;
    } catch (error) {
      // Handle InteractionRequiredAuthError - session doesn't exist in Azure AD
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn("⚠️ AuthMonitorLite: Session expired (interaction required)", {
          errorCode: error.errorCode,
        });
        // Don't spam logs - this is expected when session expires
        this.cachedStatus = { status: "unknown", message: "Session expired" };
        return this.cachedStatus;
      }
      
      logger.warn("⚠️ Auth check failed", error);
      this.cachedStatus = { status: "unknown", message: "Check failed" };
      return this.cachedStatus;
    }
  }

  /**
   * Simple token refresh attempt
   */
  async refreshToken() {
    if (!this.msalInstance || !this.currentAccount) {
      return false;
    }

    try {
      // Verify account still exists before attempting refresh
      const accounts = this.msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        logger.warn("⚠️ AuthMonitorLite: No accounts found for token refresh");
        return false;
      }

      // Update currentAccount if it's stale
      const activeAccount = accounts.find(
        (acc) => acc.homeAccountId === this.currentAccount?.homeAccountId
      ) || accounts[0];
      
      if (activeAccount.homeAccountId !== this.currentAccount?.homeAccountId) {
        logger.log("🔄 AuthMonitorLite: Account reference updated during refresh");
        this.currentAccount = activeAccount;
      }

      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: ["User.Read", "Mail.Send"],
        account: this.currentAccount,
        forceRefresh: true,
      });

      if (tokenResponse && tokenResponse.accessToken) {
        // Clear cache to force fresh check
        this.cachedStatus = null;
        this.lastCheck = null;

        logger.log("✅ Token refresh successful");
        return true;
      }
      return false;
    } catch (error) {
      // Handle InteractionRequiredAuthError - session doesn't exist in Azure AD
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn("⚠️ AuthMonitorLite: Session expired - interaction required for refresh", {
          errorCode: error.errorCode,
        });
        // Don't log as error - this is expected when session expires
        return false;
      }
      
      logger.warn("⚠️ Token refresh failed", error);
      return false;
    }
  }

  /**
   * Check if we should warn user before critical operations
   */
  async validateBeforeOperation() {
    const status = await this.checkAuthStatus();

    if (status.status === "expired") {
      toast.error("Session expired. Please refresh the page.");
      return false;
    }

    if (status.status === "critical") {
      // Silent refresh - no toast (15 min is too early to warn user)
      logger.log(`🔄 Proactive token refresh (${Math.round(status.expiresIn)} min remaining)`);
      const refreshed = await this.refreshToken();
      if (!refreshed) {
        toast.error("Session refresh failed. Please refresh the page if you encounter issues.");
        return false;
      }
    }

    return true;
  }
}

// Create singleton instance
const authMonitorLite = new AuthMonitorLite();

export default authMonitorLite;
